import Quill from "quill";
import DOMPurify from "dompurify";
import renameStyleAttributes from "../functions/renameStyleAttributes";

const Clipboard = Quill.import("modules/clipboard");
const Delta = Quill.import("delta");

// Quill's clipboard, sanitising markup before Quill reads it. The Content-Security-Policy blocks
// style attributes, so they pass through sanitising under another name and are applied through
// the CSSOM, where Quill reads the colour, bold and italic formats.
export default class SanitizingClipboard extends Clipboard {
  constructor(quill, options) {
    super(quill, options);
    this.purifier = this._buildPurifier();
  }

  // Quill's own handler pastes into the page, where the policy would block the styles.
  onPaste(event) {
    const html = event.clipboardData && event.clipboardData.getData("text/html");
    if (event.defaultPrevented || !this.quill.isEnabled() || !html) {
      super.onPaste(event);
      return;
    }

    event.preventDefault();
    const range = this.quill.getSelection(true);
    const delta = new Delta().retain(range.index).concat(this.convert(html)).delete(range.length);

    this.quill.updateContents(delta, Quill.sources.USER);
    this.quill.setSelection(delta.length() - range.length, Quill.sources.SILENT);
    this.quill.scrollIntoView();
  }

  convert(html) {
    if (typeof html !== "string") {
      return super.convert(html);
    }

    const sanitized = this.purifier.sanitize(renameStyleAttributes(html, this.styleAttribute), this.allowedMarkup);
    // Quill drops the whitespace between tags when it converts a string itself.
    this.container.innerHTML = sanitized.replace(/>\r?\n +</g, "><");
    this._applyStyleAttributes();

    return super.convert();
  }

  get styleAttribute() {
    return "data-csp-style";
  }

  // The editor's own markup, plus a paste's block elements so they stay separate lines.
  get allowedMarkup() {
    return {
      ALLOWED_TAGS: [
        "p", "br", "strong", "b", "em", "i", "u", "s", "sub", "sup", "span", "a", "ul", "ol", "li",
        "div", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote"
      ],
      ALLOWED_ATTR: [this.styleAttribute, "class", "href", "target", "rel"],
      ALLOWED_URI_REGEXP: /^(?:https?|mailto):/i,
      // These are not URLs, so they skip the check ALLOWED_URI_REGEXP applies.
      ADD_URI_SAFE_ATTR: ["target", "rel", this.styleAttribute],
      ALLOW_ARIA_ATTR: false,
      ALLOW_DATA_ATTR: false
    };
  }

  _buildPurifier() {
    // eslint-disable-next-line new-cap -- an instance of its own, so its hook applies to no other caller
    const purifier = DOMPurify(window);
    purifier.addHook("uponSanitizeAttribute", (_node, data) => this._filterAttribute(data));

    return purifier;
  }

  // Any class or style beyond the editor's formats could load a resource (background-image: url())
  // while the markup is in the page.
  _filterAttribute(data) {
    if (data.attrName === "class") {
      data.attrValue = this._keepAlignmentClasses(data.attrValue);
    } else if (data.attrName === this.styleAttribute) {
      data.attrValue = this._keepFormattingStyles(data.attrValue);
    } else {
      return;
    }

    data.keepAttr = data.attrValue.length > 0;
  }

  _keepAlignmentClasses(classes) {
    return classes.split(/\s+/).filter((name) => /^ql-align-(?:center|right|justify)$/.test(name)).join(" ");
  }

  // A detached element parses the declarations, dropping invalid values, and loads nothing.
  _keepFormattingStyles(style) {
    const parsed = document.createElement("span").style;
    parsed.cssText = style;

    return ["color", "background-color", "font-weight", "font-style"]
      .filter((property) => parsed.getPropertyValue(property))
      .map((property) => `${property}: ${parsed.getPropertyValue(property)};`)
      .join(" ");
  }

  _applyStyleAttributes() {
    this.container.querySelectorAll(`[${this.styleAttribute}]`).forEach((element) => {
      element.style.cssText = element.getAttribute(this.styleAttribute);
      element.removeAttribute(this.styleAttribute);
    });
  }
}
